import { ArrowDown, CircleAlert, Network, Wallet, type LucideIcon } from "lucide-react";
import { useId, type ReactNode } from "react";

import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { BUY_LIMIT_KEYS, type LimitHintKeys } from "../../constants/cryptoSwaps.constants";
import { useAmountFormatter } from "../../hooks/useAmountFormatter";
import { useAmountIssueText } from "../../hooks/useAmountIssueText";
import type { OtcAmountAnchor, OtcPair, OtcQuote } from "../../types/cryptoSwaps.types";
import { isFieldError, type AmountIssue } from "../../utils/cryptoSwapAmountIssues";
import type { AmountBounds } from "../../utils/cryptoSwapAmounts";
import type { ConverterField } from "../../utils/cryptoSwapConverter";
import { limitPrecision, type PairPrecision } from "../../utils/cryptoSwapFormat";
import { AssetAvatar } from "../shared/CryptoSwapBits";
import { ConverterAmountInput } from "./ConverterAmountInput";

export interface AmountFieldsBalance {
  available: string | null;
  isLoading: boolean;
  hasError: boolean;
  missing: boolean;
  /** Staff have not picked an organization yet, so there is no wallet to read. */
  needsOrganization: boolean;
}

export interface AmountFieldsProps {
  pair: OtcPair;
  sendField: ConverterField;
  receiveField: ConverterField;
  onSendChange: (value: string) => void;
  onReceiveChange: (value: string) => void;
  sendDecimals: number;
  receiveDecimals: number;
  /** The published per-purchase limits, in the receive currency. */
  receiveBounds: AmountBounds | null;
  /** The published per-purchase limits, in the send currency. */
  sendBounds: AmountBounds | null;
  sendIssue: AmountIssue | null;
  receiveIssue: AmountIssue | null;
  quote: OtcQuote | null;
  isPricing: boolean;
  networkLabel: string;
  balance: AmountFieldsBalance;
  debitDiffers: boolean;
  titleAction?: ReactNode;
  disabled?: boolean;
}

/** COP leaving the Coloca balance on top, USDT arriving below: the arrow is a direction, not a flip. */
export function AmountFields({
  pair,
  sendField,
  receiveField,
  onSendChange,
  onReceiveChange,
  sendDecimals,
  receiveDecimals,
  receiveBounds,
  sendBounds,
  sendIssue,
  receiveIssue,
  quote,
  isPricing,
  networkLabel,
  balance,
  debitDiffers,
  titleAction,
  disabled,
}: AmountFieldsProps) {
  const { t, activeLanguage } = useTranslations("cryptoSwaps");
  const format = useAmountFormatter(pair);
  const issueText = useAmountIssueText(pair);
  const titleId = useId();
  const sendId = useId();
  const receiveId = useId();
  const sendHintId = useId();
  const receiveHintId = useId();

  const send = pair.send_currency;
  const receive = pair.receive_currency;
  const balanceShown = sendIssue?.kind !== "balance" && sendIssue?.kind !== "no_wallet";

  const priceLine = quote
    ? t("price_line", {
        asset: quote.receive.currency,
        price: format(quote.price.value, quote.price.currency),
        currency: quote.price.currency,
      })
    : null;

  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id={titleId} className="text-lg font-semibold tracking-tight text-foreground">
          {t("amount_section_title")}
        </h2>
        {titleAction}
      </div>

      <div className="flex flex-col">
        <AmountBox
          id={sendId}
          label={t("amount_send_label")}
          currency={send}
          kind="FIAT"
          field={sendField}
          onChange={onSendChange}
          decimals={sendDecimals}
          locale={activeLanguage}
          invalid={isFieldError(sendIssue)}
          describedBy={sendHintId}
          disabled={disabled}
        >
          <div id={sendHintId} className="flex flex-col gap-1">
            {sendIssue && <IssueLine issue={sendIssue}>{issueText(sendIssue)}</IssueLine>}
            {balanceShown && <BalanceLine balance={balance} pair={pair} />}
            <LimitsLine side="send" bounds={sendBounds} pair={pair} />
            {debitDiffers && quote && (
              <FieldNote>
                {t("amount_debit_note", {
                  amount: format(quote.send.amount, quote.send.currency),
                  currency: quote.send.currency,
                })}
              </FieldNote>
            )}
          </div>
        </AmountBox>

        <FlowSpine pendingText={isPricing ? t("amount_pricing") : null} priceLine={priceLine} />

        <AmountBox
          id={receiveId}
          label={t("amount_receive_label")}
          labelAside={
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/60 px-2.5 py-0.5 text-xs font-medium text-foreground">
              <Network aria-hidden="true" className="size-3.5 text-muted-foreground" />
              {networkLabel}
            </span>
          }
          currency={receive}
          kind="ONCHAIN"
          field={receiveField}
          onChange={onReceiveChange}
          decimals={receiveDecimals}
          quotedMinFractionDigits={2}
          locale={activeLanguage}
          invalid={isFieldError(receiveIssue)}
          describedBy={receiveHintId}
          disabled={disabled}
        >
          <div id={receiveHintId} className="flex flex-col gap-1">
            {receiveIssue && <IssueLine issue={receiveIssue}>{issueText(receiveIssue)}</IssueLine>}
            <LimitsLine side="receive" bounds={receiveBounds} pair={pair} />
          </div>
        </AmountBox>
      </div>
    </section>
  );
}

export function AmountBox({
  id,
  label,
  labelAside,
  currency,
  kind,
  field,
  onChange,
  decimals,
  quotedMinFractionDigits = 0,
  locale,
  invalid,
  describedBy,
  disabled,
  children,
}: {
  id: string;
  label: string;
  labelAside?: ReactNode;
  currency: string;
  kind: "FIAT" | "ONCHAIN";
  field: ConverterField;
  onChange: (value: string) => void;
  decimals: number;
  quotedMinFractionDigits?: number;
  locale: string;
  invalid: boolean;
  describedBy?: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  const pending = field.source === "pending";

  return (
    <div
      data-invalid={invalid || undefined}
      className={cn(
        "group/amount flex flex-col gap-1 rounded-2xl border bg-card px-5 pt-4 pb-3.5 transition-colors",
        invalid
          ? "border-destructive bg-destructive/5 ring-3 ring-destructive/15 dark:bg-destructive/10"
          : "border-border focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50",
        disabled && "opacity-60",
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label
          htmlFor={id}
          className={cn("text-[13px] font-medium", invalid ? "text-destructive" : "text-muted-foreground")}
        >
          {label}
        </label>
        {labelAside}
      </div>
      <div className="flex items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <ConverterAmountInput
            id={id}
            value={field.value}
            onValueChange={onChange}
            decimals={decimals}
            minFractionDigits={field.source === "quoted" ? quotedMinFractionDigits : 0}
            locale={locale}
            placeholder="0"
            disabled={disabled}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            aria-busy={pending || undefined}
            className={cn(
              "h-12 rounded-none border-0 bg-transparent px-0 py-0 text-[28px] font-medium tracking-tight shadow-none focus-visible:ring-0 disabled:opacity-100 aria-invalid:ring-0 md:text-[32px] dark:bg-transparent",
              invalid && "text-destructive",
            )}
          />
          {pending && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-2.5 left-0 w-40 group-focus-within/amount:hidden"
            >
              <ColocaSkeleton className="size-full rounded-lg" />
            </span>
          )}
        </div>
        <span className="inline-flex shrink-0 items-center gap-2 rounded-full border border-border bg-background py-1 pr-3 pl-1 text-sm font-semibold">
          <AssetAvatar asset={currency} kind={kind} size="sm" />
          {currency}
        </span>
      </div>
      {children}
    </div>
  );
}

export function FlowSpine({
  pendingText,
  priceLine,
}: {
  pendingText: string | null;
  priceLine: string | null;
}) {
  return (
    <div className="flex min-h-11 items-stretch gap-3 px-5">
      <span aria-hidden="true" className="flex w-4 flex-col items-center">
        <span className="w-px flex-1 bg-border" />
        <ArrowDown className="my-0.5 size-4 shrink-0 text-muted-foreground" />
        <span className="w-px flex-1 bg-border" />
      </span>
      <p className="self-center font-mono text-[13px] text-muted-foreground tabular-nums">
        {pendingText}
        <span aria-live="polite">{pendingText ? "" : priceLine}</span>
      </p>
    </div>
  );
}

function BalanceLine({ balance, pair }: { balance: AmountFieldsBalance; pair: OtcPair }) {
  const { t } = useTranslations("cryptoSwaps");
  const format = useAmountFormatter(pair);
  const currency = pair.send_currency;

  if (balance.needsOrganization) return <FieldNote icon={Wallet}>{t("balance_pick_org")}</FieldNote>;
  if (balance.missing) return null;

  const detail = balance.isLoading
    ? t("balance_loading")
    : balance.hasError || !balance.available
      ? t("balance_error")
      : t("balance_available", { amount: format(balance.available, currency), currency });

  return (
    <FieldNote icon={Wallet}>
      {t("amount_send_source")} <span aria-hidden="true">·</span>{" "}
      <span className="font-medium text-foreground tabular-nums">{detail}</span>
    </FieldNote>
  );
}

export function LimitsLine({
  side,
  bounds,
  pair,
  limitKeys = BUY_LIMIT_KEYS,
}: {
  side: OtcAmountAnchor;
  bounds: AmountBounds | null;
  pair: PairPrecision;
  limitKeys?: LimitHintKeys;
}) {
  const { t } = useTranslations("cryptoSwaps");
  const format = useAmountFormatter(pair);
  if (!bounds) return null;

  const currency = side === "send" ? pair.send_currency : pair.receive_currency;
  const precision = limitPrecision(currency, pair);
  const min = bounds.min ? format(bounds.min, currency, precision) : null;
  const max = bounds.max ? format(bounds.max, currency, precision) : null;
  const keys = limitKeys[side];
  const text =
    min && max
      ? t(keys.both, { min, max, currency })
      : min
        ? t(keys.min, { min, currency })
        : max
          ? t(keys.max, { max, currency })
          : null;

  return text ? <FieldNote>{text}</FieldNote> : null;
}

export function IssueLine({ issue, children }: { issue: AmountIssue; children: ReactNode }) {
  if (!isFieldError(issue)) return <FieldNote>{children}</FieldNote>;
  return (
    <p className="flex items-start gap-1.5 text-xs leading-relaxed font-medium text-destructive">
      <CircleAlert aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

export function FieldNote({ icon: Icon, children }: { icon?: LucideIcon; children: ReactNode }) {
  return (
    <p className="flex items-start gap-1.5 text-xs leading-relaxed text-muted-foreground">
      {Icon && <Icon aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />}
      <span>{children}</span>
    </p>
  );
}
